import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import {
  downloadSystemDashboard,
  systemDashboardInfiniteQueryOptions,
} from '../api/dashboards.api';
import { ExportButton } from '@/components/common/ExportButton';
import { ReportingPeriodFilterPanel } from '../components/ReportingPeriodFilterPanel';
import { getSystemDashboardColumns } from '../components/SystemDashboardColumns';
import { useReportingPeriodFilters } from '../hooks/useReportingPeriodFilters';

export function SystemDashboardPage() {
  const controller = useReportingPeriodFilters('system');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(systemDashboardInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedUserId = useRevealLastViewedRow('systemDashboardMembers');

  const columns = useMemo(() => getSystemDashboardColumns(controller.params), [controller.params]);

  const totals = firstPageMeta?.totals;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="System Dashboard"
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              onExport={() => downloadSystemDashboard(controller.params)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ReportingPeriodFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the dashboard"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            isFetching={isRefetching}
            stats={[
              {
                key: 'dcrEntered',
                label: 'DCR Entered',
                value: totals?.dcrEntered,
              },
              {
                key: 'dcrApproved',
                label: 'DCR Approved',
                value: totals?.dcrApproved,
              },
              {
                key: 'derEntered',
                label: 'DER Entered',
                value: totals?.derEntered,
              },
              {
                key: 'dcrClaimedAmount',
                label: 'DCR Claimed',
                value: totals?.dcrClaimedAmount,
                unit: TAKA_SYMBOL,
              },
              {
                key: 'derClaimedAmount',
                label: 'DER Claimed',
                value: totals?.derClaimedAmount,
                unit: TAKA_SYMBOL,
              },
              {
                key: 'deliveredQty',
                label: 'SD Delivered',
                value: totals?.deliveredQty,
              },
            ]}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="No activity for these filters"
            emptyHint="Try a wider date range."
            isRowHighlighted={(member) => member.userId === lastViewedUserId}
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
